import React from 'react';
import {
  FieldValues,
  Path,
  useController,
  useFormContext,
} from 'react-hook-form';
import FieldError from './FieldError';
import { twJoin, twMerge } from 'tailwind-merge';
import FieldLabel from './FieldLabel';
import FieldDescribe from './FieldDescribe';
import InputLength from './InputLength';

type ControlledInputProps<TFormValues extends FieldValues> = {
  name: Path<TFormValues>;
  label: string | React.ReactNode;
  labelClassName?: string;
  classNameWrapper?: string;
  describe?: React.ReactNode;
  isRequired?: boolean;
  withLength?: boolean;
  withoutTouch?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>;

const FormInput = <TFormValues extends FieldValues>({
  name,
  label,
  className,
  classNameWrapper,
  labelClassName,
  isRequired,
  describe,
  maxLength,
  disabled,
  withLength,
  withoutTouch = true,
  ...props
}: ControlledInputProps<TFormValues>) => {
  const { register, control } = useFormContext<TFormValues>();

  const {
    field,
    fieldState: { error, isTouched },
  } = useController({ name, control });

  const touch = withoutTouch || isTouched;

  return (
    <div className={twMerge('flex w-full flex-col gap-0.5', classNameWrapper)}>
      <FieldLabel
        className={labelClassName}
        name={name}
        label={label}
        isRequired={isRequired}
      />
      <input
        {...register(name)}
        {...props}
        disabled={disabled}
        id={name}
        maxLength={maxLength}
        className={twJoin(
          className,
          'border text-btn-black no-spin border-med-gray-3 text-sm h-12 placeholder-med-gray-3 placeholder:font-medium outline-none font-medium focus:border-blue-600 rounded-5 py-1.5 px-3',
          error && touch && '!border-alert-negative bg-error-yellow',
          disabled && 'opacity-50',
        )}
      />
      {error && touch && <FieldError>{error?.message as string}</FieldError>}
      {!error && describe && <FieldDescribe>{describe}</FieldDescribe>}
      {!error && withLength && maxLength && (
        <InputLength
          maxLength={maxLength}
          currentLength={field?.value?.length ?? 0}
        />
      )}
    </div>
  );
};

export default FormInput;
